import React from 'react';
import { useTranslation } from 'react-i18next';
import BlazeTable from 'components/blazeTable/BlazeTable';
import { BlazeTableColDef, BlazeTableOptions } from 'components/blazeTable/BlazeTable.types';
import BzmIcon from 'components/icons/BzmIcon';
import { BZMIconClasses } from '@blazemeter/blaze-ct-ui-components';
import { SortConfiguration } from 'types/rest/paginationTypes';
import { formatNumberWithSpaces } from 'utils/stringUtils';
import { formatDateAndTime } from 'utils/dateUtil';
import { PAGE_SIZE_OPTIONS, UsageReportRow, UsageReportSort, UsageReportSortField, UsageReportStatus } from './types';

interface UsageReportTableProps {
  rows: UsageReportRow[];
  isLoading: boolean;
  page: number;
  pageSize: number;
  totalRows: number;
  sort: UsageReportSort;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSort: (sort: UsageReportSort) => void;
}

interface StatusBadgeConfig {
  modifier: string;
  iconVariant: BZMIconClasses | string;
  labelKey: string;
}

const STATUS_BADGE_CONFIG: Record<UsageReportStatus, StatusBadgeConfig> = {
  ACTIVE: { modifier: 'active', iconVariant: 'icon-bzm-checkmark', labelKey: 'usageReport.status.active' },
  IDLE: { modifier: 'idle', iconVariant: 'icon-bzm-play-circle', labelKey: 'usageReport.status.idle' },
  NEVER_USED: { modifier: 'never-used', iconVariant: 'icon-bzm-caution', labelKey: 'usageReport.status.neverUsed' },
  DELETED: { modifier: 'deleted', iconVariant: 'icon-bzm-delete', labelKey: 'usageReport.status.deleted' },
};

// Accessor name on BlazeTable column ↔ API sort field
const SORT_FIELD_BY_ACCESSOR: Record<string, UsageReportSortField> = {
  serviceName: 'SERVICE_NAME',
  deployedAt: 'DEPLOYED_AT',
  lastUsedAt: 'LAST_USED',
  hits: 'HITS',
  misses: 'MISSES',
  total: 'TOTAL',
};

const ACCESSOR_BY_SORT_FIELD: Record<UsageReportSortField, string> = {
  SERVICE_NAME: 'serviceName',
  DEPLOYED_AT: 'deployedAt',
  LAST_USED: 'lastUsedAt',
  HITS: 'hits',
  MISSES: 'misses',
  TOTAL: 'total',
};

const renderDateCell = (value: string | null): string => (value ? formatDateAndTime(new Date(value)) : '—');

// Server-side rows already arrive in the correct (page × size) slice, so the
// active page is always 1 from the BlazeTable pager's perspective. We translate
// to/from zero-based on the wire via onPageChange (BlazeTable emits 1-based).
const UsageReportTable: React.FC<UsageReportTableProps> = ({
  rows,
  isLoading,
  page,
  pageSize,
  totalRows,
  sort,
  onPageChange,
  onPageSizeChange,
  onSort,
}) => {
  const { t } = useTranslation('admin');

  const renderStatusCell = (row: UsageReportRow) => {
    const config = STATUS_BADGE_CONFIG[row.status];
    return (
      <span className={`usage-status-badge usage-status-badge--${config.modifier}`}>
        <BzmIcon variant={config.iconVariant} className="usage-status-badge__icon" />
        {t(config.labelKey)}
      </span>
    );
  };

  const columnDef: BlazeTableColDef[] = [
    { header: t('usageReport.columns.serviceName'), accessor: 'serviceName' },
    { header: t('usageReport.columns.workspace'), accessor: 'workspaceName', isSortable: false },
    { header: t('usageReport.columns.location'), accessor: 'locationName', isSortable: false },
    { header: t('usageReport.columns.createdBy'), accessor: 'createdBy', isSortable: false },
    {
      header: t('usageReport.columns.deployedAt'),
      accessor: 'deployedAt',
      cellComponent: (row: UsageReportRow) => renderDateCell(row.deployedAt),
    },
    {
      header: t('usageReport.columns.lastUsed'),
      accessor: 'lastUsedAt',
      cellComponent: (row: UsageReportRow) => renderDateCell(row.lastUsedAt),
    },
    {
      header: t('usageReport.columns.hits'),
      accessor: 'hits',
      cellComponent: (row: UsageReportRow) => formatNumberWithSpaces(row.hits),
    },
    {
      header: t('usageReport.columns.misses'),
      accessor: 'misses',
      cellComponent: (row: UsageReportRow) => formatNumberWithSpaces(row.misses),
    },
    {
      header: t('usageReport.columns.total'),
      accessor: 'total',
      cellComponent: (row: UsageReportRow) => formatNumberWithSpaces(row.total),
    },
    {
      header: t('usageReport.columns.status'),
      accessor: 'status',
      cellComponent: renderStatusCell,
      isSortable: false,
    },
  ];

  const options: BlazeTableOptions = {
    columnDef,
    selectable: false,
    pageable: true,
    showRowExpander: false,
    tableName: 'usage-report',
    noResultsText: t('usageReport.empty'),
    defaultEmpty: t('usageReport.empty'),
    loadingText: t('usageReport.empty'),
    paginationOverride: {
      pageSize,
      total: totalRows,
      activePage: page + 1, // BlazeTable pager is 1-based; our state is 0-based
    },
    sortable: true,
    sortingOverride: {
      sortKey: ACCESSOR_BY_SORT_FIELD[sort.field],
      direction: sort.direction === 'ASC' ? 'asc' : 'desc',
    },
  };

  const handlePageChange = (oneBasedPage: number) => {
    onPageChange(oneBasedPage - 1);
  };

  const handleSort = (sortConfig: SortConfiguration) => {
    const field = SORT_FIELD_BY_ACCESSOR[sortConfig.sortKey];
    if (!field) {
      return; // non-sortable column header clicked — ignore
    }
    onSort({ field, direction: sortConfig.direction === 'asc' ? 'ASC' : 'DESC' });
  };

  return (
    <BlazeTable
      data={rows}
      options={options}
      isLoading={isLoading}
      pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
      onPageChange={handlePageChange}
      onPageSizeChange={onPageSizeChange}
      onSort={handleSort}
      showPageSizeDropdown={true}
    />
  );
};

export default UsageReportTable;
